import {
  Alert,
  AlertDescription,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@atlas/core";
import { useSessionData, useTranslation, useZodSearchParams } from "@atlas/sdk";
import { useHubCurrentCart, useHubPackage } from "@query";
import { defaultHubCartPackageId } from "@shared";
import { useCreatePackageSave, useEditPackageSave } from "@shared/hooks";
import { Info, ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { EditPackageDialog } from "../../../package-manager/pages/EditPackageDialog";
import { CheckoutCatalogs } from "./CheckoutCatalogs";
import { PackageSummaryCard } from "./PackageSummaryCard";

const checkoutSearchParamsSchema = z.object({
  packageId: z.coerce.number().optional(),
});

export function PackageCheckout() {
  const { t } = useTranslation();
  const { packageId } = useZodSearchParams(checkoutSearchParamsSchema);
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart, isLoading: cartUserLoading } = useHubCurrentCart(
    uid,
    { enabled: uid.length > 0 },
  );

  const defaultHubCartId = defaultHubCartPackageId(userHubCart);
  const hubCartPackageId = packageId ?? defaultHubCartId;
  const { data: cartPackage, isLoading: packageLoading } = useHubPackage(
    hubCartPackageId,
    { enabled: hubCartPackageId > 0 },
  );

  const isLoading = useMemo(
    () => cartUserLoading || (hubCartPackageId > 0 && packageLoading),
    [cartUserLoading, hubCartPackageId, packageLoading],
  );

  const catalogRelations = useMemo(
    () => cartPackage?.catalogRelations ?? [],
    [cartPackage],
  );

  const packageSummaryTitle = useMemo(() => {
    const pkg = cartPackage;
    return (
      (pkg?.label ?? pkg?.name ?? "").trim() ||
      t("hub.checkout.summary_card_title_fallback", {
        defaultValue: "Checkout summary",
      })
    );
  }, [cartPackage, t]);

  const [editPackageOpen, setEditPackageOpen] = useState(false);
  const [createPackageOpen, setCreatePackageOpen] = useState(false);

  const { handleEditPackageSave, isEditPackagePending } = useEditPackageSave(
    cartPackage,
    setEditPackageOpen,
  );

  const { handleCreatePackageSave, isCreatePending } =
    useCreatePackageSave(setCreatePackageOpen);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex xl:flex-row max-lg:flex-col gap-6 p-6 min-h-0 flex-1">
        <ScrollArea className="flex-1 min-h-0 min-w-50">
          {!isLoading && catalogRelations.length === 0 ? (
            <Empty className="mx-auto min-h-0 max-w-3xl flex-1">
              <EmptyMedia>
                <ShoppingCart className="h-6 w-6 text-muted-foreground" />
              </EmptyMedia>
              <EmptyHeader>
                <EmptyTitle>
                  {t("hub.checkout.empty", {
                    defaultValue: "Your package is empty.",
                  })}
                </EmptyTitle>
                <EmptyDescription>
                  {t("hub.checkout.empty_description", {
                    defaultValue:
                      "Explore the catalog to add solutions and add-ons to your package.",
                  })}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <CheckoutCatalogs
              formId="package-checkout-form"
              catalogs={catalogRelations}
              currentPackage={cartPackage}
            />
          )}
        </ScrollArea>
        <div className="min-w-0 shrink-0 lg:w-[450px] lg:sticky self-start">
          <PackageSummaryCard
            formId="package-checkout-form"
            isPopover={false}
            titleLabel={packageSummaryTitle}
            itemCount={catalogRelations.length}
            createdAtIso={cartPackage?.createdByDatetime ?? null}
            submitDisabled={catalogRelations.length === 0}
            cartPackage={cartPackage}
            onEdit={() => setEditPackageOpen(true)}
            onCreate={() => setCreatePackageOpen(true)}
            visibility={cartPackage?.visibility}
          />
          {catalogRelations.length > 0 && (
            <Alert variant="info" className="mt-4">
              <Info className="size-4" />
              <AlertDescription>
                {t("hub.subscribe_wizard.check_solution_tokens", {
                  defaultValue:
                    "Please ensure the catalogs in your package have valid tokens, otherwise the installation may fail.",
                })}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <EditPackageDialog
          open={editPackageOpen || createPackageOpen}
          variant={createPackageOpen ? "create" : "edit"}
          pkg={createPackageOpen ? null : (cartPackage ?? null)}
          onOpenChange={(open) => {
            if (!open) {
              setEditPackageOpen(false);
              setCreatePackageOpen(false);
            }
          }}
          isSubmitting={
            createPackageOpen ? isCreatePending : isEditPackagePending
          }
          onSave={
            createPackageOpen ? handleCreatePackageSave : handleEditPackageSave
          }
        />
      </div>
    </div>
  );
}
